// The 6-digit email code steps, in the site kit (docs/plans/viewing-first-onboarding.md, phase 3): email → code → a new
// password → signed in. The website's viewing thank-you page starts at the code (it was emailed with the booking);
// Forgot password on the login page starts at the email. POST /auth/code/request · /auth/code/verify · /auth/password/set.
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { ApiRequestError } from '../../lib/api';
import { emailCodesApi, RESEND_COOLDOWN_SECONDS } from '../../lib/emailCodes';
import { useAuth } from '../../stores/auth';
import { AuthSubmit } from '../AuthShell';
import { Field, Input } from './Form';
import { PasswordField, passwordScore } from '../../pages/ChangePassword';

const CODE_LENGTH = 6;
const MIN_PASSWORD = 10;
const LABELS = ['Too short', 'Weak', 'Fair', 'Good', 'Strong', 'Very strong'];
const messageOf = (e: unknown) => (e instanceof ApiRequestError ? e.message : 'Something went wrong. Please try again.');

/** Seconds left before another code may be asked for; `start()` begins the wait. */
export function useCooldown(seconds = RESEND_COOLDOWN_SECONDS) {
  const [until, setUntil] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (until <= now) return;
    const t = window.setTimeout(() => setNow(Date.now()), 500);
    return () => window.clearTimeout(t);
  }, [until, now]);
  const left = Math.max(0, Math.ceil((until - now) / 1000));
  return { left, start: () => { const t = Date.now(); setNow(t); setUntil(t + seconds * 1000); } };
}

/** Six boxes, one digit each; typing moves on, Backspace moves back, a pasted code fills them all. Complete → onComplete. */
export function CodeBoxes({ value, onChange, onComplete, disabled, invalid }: {
  value: string; onChange: (v: string) => void; onComplete?: (code: string) => void; disabled?: boolean; invalid?: boolean;
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  // Empty (at first, or cleared after a wrong code) and usable: the cursor goes to the first box.
  useEffect(() => { if (!disabled && !value) refs.current[0]?.focus(); }, [disabled, value]);
  const put = (from: number, digits: string) => {
    const chars = value.padEnd(CODE_LENGTH, ' ').split('');
    let i = from;
    for (const d of digits.replace(/\D/g, '')) { if (i >= CODE_LENGTH) break; chars[i++] = d; }
    const next = chars.join('').replace(/\s+$/, '');
    onChange(next);
    refs.current[Math.min(i, CODE_LENGTH - 1)]?.focus();
    if (/^\d{6}$/.test(next)) onComplete?.(next);
  };
  return (
    <div className="flex justify-between gap-2 sm:gap-3" role="group" aria-label="6-digit code">
      {Array.from({ length: CODE_LENGTH }, (_, i) => (
        <input
          key={i} ref={(el) => { refs.current[i] = el; }} value={value[i]?.trim() ?? ''} disabled={disabled}
          inputMode="numeric" autoComplete={i === 0 ? 'one-time-code' : 'off'} maxLength={CODE_LENGTH} aria-label={`Digit ${i + 1}`}
          className={`site-input !h-14 !w-full !min-w-0 !px-0 text-center font-display !text-[26px] ${invalid ? '!border-[var(--status-critical)]' : ''}`}
          onChange={(e) => put(i, e.target.value.slice(value[i]?.trim() ? 1 : 0) || '')}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !value[i]?.trim() && i > 0) {
              e.preventDefault();
              onChange(value.slice(0, i - 1));
              refs.current[i - 1]?.focus();
            } else if (e.key === 'Backspace') {
              e.preventDefault();
              onChange(value.slice(0, i));
            }
          }}
          onPaste={(e) => { e.preventDefault(); put(i, e.clipboardData.getData('text')); }}
        />
      ))}
    </div>
  );
}

type Stage = 'email' | 'code' | 'password';

/**
 * Email → code → password → signed in (the session kept in the auth store, as LoginPage does). `start="code"` when the
 * code was already sent (the viewing booking); `intro` replaces the line above the code boxes.
 */
export function EmailCodeSteps({ email: initialEmail = '', start = 'email', intro, submitLabel = 'Save and sign in', onSignedIn }: {
  email?: string; start?: Stage; intro?: ReactNode; submitLabel?: string; onSignedIn: () => void;
}) {
  const startSession = useAuth((s) => s.startSession);
  const [stage, setStage] = useState<Stage>(start);
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resent, setResent] = useState(false);
  const cooldown = useCooldown();
  // The booking just sent one: wait before offering another.
  useEffect(() => { if (start === 'code') cooldown.start(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError(null);
    try { await fn(); } catch (e) { setError(messageOf(e)); } finally { setBusy(false); }
  };
  const send = (e?: FormEvent) => {
    e?.preventDefault();
    return run(async () => { await emailCodesApi.request(email.trim()); setCode(''); setStage('code'); cooldown.start(); });
  };
  const verify = (c: string) => run(async () => {
    try { setToken((await emailCodesApi.verify(email.trim(), c)).token); setStage('password'); }
    catch (e) { setCode(''); throw e; }
  });
  const save = (e: FormEvent) => {
    e.preventDefault();
    if (password.length < MIN_PASSWORD) { setError(`Use at least ${MIN_PASSWORD} characters.`); return; }
    return run(async () => {
      const session = await emailCodesApi.setPassword(token, password);
      startSession(session);
      onSignedIn();
    });
  };
  const resend = () => run(async () => { await emailCodesApi.request(email.trim()); setCode(''); setResent(true); cooldown.start(); });
  const note = error && <p className="site-error" role="alert">{error}</p>;

  if (stage === 'email') {
    return (
      <form onSubmit={send} className="space-y-5">
        <Field label="Email">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="username" />
        </Field>
        {note}
        <div className="pt-1"><AuthSubmit disabled={busy || !email.trim()}>{busy ? 'Sending…' : 'Email me a code'}</AuthSubmit></div>
      </form>
    );
  }

  if (stage === 'code') {
    return (
      <form onSubmit={(e) => { e.preventDefault(); if (/^\d{6}$/.test(code)) void verify(code); }} className="space-y-5">
        <p className="text-[15px] leading-relaxed text-ink-600">
          {intro ?? <>If there is an account for <span className="font-medium text-ink-900">{email}</span>, we've emailed it a 6-digit code. It works for 15 minutes.</>}
        </p>
        <div>
          <span className="site-label mb-2 block">Your code</span>
          <CodeBoxes value={code} onChange={setCode} onComplete={(c) => void verify(c)} disabled={busy} invalid={!!error} />
        </div>
        {note}
        <div className="pt-1"><AuthSubmit disabled={busy || code.length !== CODE_LENGTH}>{busy ? 'Checking…' : 'Continue'}</AuthSubmit></div>
        <p className="text-center text-[14px] text-ink-500">
          {cooldown.left > 0
            ? <>{resent ? 'A new code is on its way. ' : 'No email? '}You can ask for another in {cooldown.left}s.</>
            : <>No email? Check your junk folder, or <button type="button" className="copy-link font-medium" onClick={() => void resend()} disabled={busy}>send a new code</button>.</>}
        </p>
      </form>
    );
  }

  const score = passwordScore(password);
  return (
    <form onSubmit={save} className="space-y-5">
      <div>
        <PasswordField site label="Choose a password" value={password} onChange={setPassword} autoComplete="new-password" hint={`At least ${MIN_PASSWORD} characters, not your email. Longer is better than complicated.`} />
        <div className="mt-2 flex items-center gap-3" aria-live="polite">
          <div className="flex h-1.5 flex-1 gap-1">
            {[1, 2, 3, 4, 5].map((i) => (
              <span key={i} className={`h-full flex-1 rounded-full ${i <= score ? (score >= 4 ? 'bg-emerald-500' : score >= 3 ? 'bg-amber-400' : 'bg-red-400') : 'bg-ink-200'}`} />
            ))}
          </div>
          <span className="w-24 text-right text-[12.5px] font-medium text-ink-500">{password ? LABELS[score] : ''}</span>
        </div>
      </div>
      {note}
      <div className="pt-1"><AuthSubmit disabled={busy || !password}>{busy ? 'Saving…' : submitLabel}</AuthSubmit></div>
    </form>
  );
}
